import React, { FunctionComponent } from 'react'
import { HostData } from '../Customer/Data'
import { BriefShell, BriefContentContext } from './BriefShell'
import { BriefLoading } from './BriefLoading'
import { Note } from './BriefSectionTypes'
import { findNote } from './briefHelpers'
import { SectionWithNote } from './SectionWithNote'
import { useBriefData } from './useBriefData'
import { useCustomerProfile } from './useCustomerProfile'
import styles from './BriefShell.module.css'

interface Props {
  teamID: string
  host: HostData
}

interface SecuritySection {
  id: string
  title?: string
  notes?: Note[]
}

interface SecurityBriefing {
  type: string
  data: { sections: SecuritySection[] }
  insights?: string[]
  createdAt: string
}

export const SecurityBrief: FunctionComponent<Props> = ({ teamID, host }) => {
  const { briefing, loading, error } = useBriefData<SecurityBriefing>({
    teamID,
    customerID: host.id,
    briefType: 'security',
    errorFallback: 'An error occurred while generating the Security brief',
  })
  const { profile, loading: profileLoading } = useCustomerProfile(teamID, host.id)

  if (loading || profileLoading) {
    return (
      <div className={`empty loading ${styles.loadingState}`} data-testid='loading-dots'>
        <div className={styles.loadingMessage}>
          <BriefLoading />
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className={`alert error ${styles.errorState}`} data-testid='security-error'>
        <div>Failed to generate Security brief: {error}</div>
      </div>
    )
  }

  const sections = briefing?.data?.sections ?? []
  const versionPlatform = sections.find(section => section.id === 'version-platform')

  const renderContent = ({ activeSideNotes, onNoteLayoutChange }: BriefContentContext): React.ReactNode =>
    versionPlatform && (
      <SectionWithNote
        activeSideNotes={activeSideNotes}
        note={findNote(sections, 'version-platform', 'version-platform')}
        segmentTestId='version-platform-segment'
        noteTestId='version-platform-note'
        onNoteLayoutChange={onNoteLayoutChange}
        defaultExpanded
      >
        <div className={styles.sectionWrapper} data-testid='version-platform-section'>
          <header className={styles.sectionHeader}>
            <h4 className={styles.sectionTitle}>{versionPlatform.title || 'Version and platform'}</h4>
          </header>
        </div>
      </SectionWithNote>
    )

  return (
    <BriefShell
      teamID={teamID}
      host={host}
      briefType='security'
      title='Security'
      renderOverview={() => null}
      generatedAt={briefing?.createdAt}
      industry={profile?.industry}
      layoutContent={briefing}
    >
      {renderContent}
    </BriefShell>
  )
}
